import { useChatStore } from '@/store/chatStore'
import { useChatHistory } from '@/hooks/useChatHistory'

export function ChatHistorySidebar() {
  const sessions         = useChatStore((s) => s.sessions)
  const isSidebarOpen    = useChatStore((s) => s.isSidebarOpen)
  const currentSessionId = useChatStore((s) => s.currentSessionId)
  const activeSessionId  = useChatStore((s) => s.activeSessionId)
  const closeSidebar     = useChatStore((s) => s.closeSidebar)
  const returnToActive   = useChatStore((s) => s.returnToActiveSession)

  const {
    loadSession,
    loadMoreSessions,
    hasMore,
    isLoadingMore,
  } = useChatHistory()

  if (!isSidebarOpen) return null

  // Show "back" button only when viewing a different session than active
  const isViewingHistory = !!activeSessionId && currentSessionId !== activeSessionId

  const handleLoadSession = (sessionId: string) => {
    loadSession(sessionId)
  }

  const handleReturnToActive = () => {
    // returnToActive restores currentSessionId + activeMessages snapshot — no API call needed
    returnToActive()
    closeSidebar()
  }

  return (
    <div className="
      absolute inset-0 z-10
      bg-[var(--color-panel)]
      flex flex-col
      rounded-b-widget
    ">
      {/* Header */}
      <div className="
        px-4 py-3 border-b border-[var(--color-border)]
        flex items-center justify-between
      ">
        <span className="text-sm font-semibold text-[var(--color-text)]">
          Chat History
        </span>
        <span className="text-xs text-[var(--color-text-sub)]">
          {sessions.length} conversation{sessions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Back to current chat banner — only when viewing history */}
      {isViewingHistory && (
        <button
          onClick={handleReturnToActive}
          className="
            w-full px-4 py-2.5
            bg-[var(--color-brand)] text-white
            text-xs font-medium text-left
            flex items-center gap-2
            hover:opacity-90 transition-opacity
          "
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to current chat
        </button>
      )}

      {/* Session list */}
      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-[var(--color-text-sub)]">
              No previous conversations.
            </p>
          </div>
        ) : (
          <>
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => handleLoadSession(session.id)}
                className={`
                  w-full text-left px-4 py-3
                  border-b border-[var(--color-border)]
                  hover:bg-[var(--color-surface)]
                  transition-colors
                  ${session.id === currentSessionId
                    ? 'bg-[var(--color-surface)] border-l-2 border-l-[var(--color-brand)]'
                    : ''}
                `}
              >
                <p className="
                  text-sm font-medium text-[var(--color-text)]
                  truncate mb-0.5
                ">
                  {session.title || 'Untitled conversation'}
                  {session.id === activeSessionId && (
                    <span className="ml-2 text-[10px] text-[var(--color-brand)] font-normal">
                      current
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-[var(--color-text-sub)]">
                  {session.createdAt.toLocaleDateString()} ·{' '}
                  {session.totalTokensUsed.toLocaleString()} tokens
                </p>
              </button>
            ))}

            {/* Load More button — only shown when more pages exist */}
            {hasMore && (
              <button
                onClick={loadMoreSessions}
                disabled={isLoadingMore}
                className="
                  w-full px-4 py-3
                  text-xs font-medium
                  text-[var(--color-brand)]
                  hover:bg-[var(--color-surface)]
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                  flex items-center justify-center gap-2
                "
              >
                {isLoadingMore ? (
                  <>
                    <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Loading...
                  </>
                ) : (
                  'Load more conversations'
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}